import { getRoot, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { callAgroApi } from '@services/agroapi';
import { computeSeedAmountPerUnitArea, isPresent, luxonUserLocale } from '@farmai-shared/LFUtils';
import { Cropzone } from './Cropzone';
import { ActivityType } from './ActivityType';
import { AnyJsonValue } from './GeoJSON';
import { Attachment } from './AttachmentStore';
import { DateTimeType, withDateProcessors } from './DateTimeUtility';
import { removeEmptyExtra } from '@services/utils';
import { soilTypeOptions } from '@app/monitoring/_components/soil/SoilDropdown';
import { SupportedMapLayers } from './enums';
import i18n from '@services/next-i18n';
import { ActivityResponse } from '@services/responseTypes';
import { Transformers } from '@services/transformer';
import { IRootStoreAny } from './rootUtilType';
export const Activity = withDateProcessors(types.model('Activity', {
  id: types.identifier,
  extra: types.maybeNull(AnyJsonValue),
  cropzone: types.safeReference(types.late(() => Cropzone), {
    get(id: string, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.currentOrganization?.cropzones.get(id);
    },
    set(value) {
      return value.id;
    }
  }),
  status: types.optional(types.maybeNull(types.string), 'default'),
  datetime: DateTimeType,
  endDatetime: types.maybeNull(DateTimeType),
  attachments: types.array(types.late(() => Attachment)),
  note: types.maybeNull(types.string),
  farm: types.model({
    id: types.string,
    name: types.string
  }),
  field: types.maybeNull(types.model({
    id: types.string,
    name: types.string
  })),
  activityType: types.safeReference(types.late(() => ActivityType), {
    get(id: string, parent: any) {
      const activityTypeStore = getRoot<IRootStoreAny>(parent).activityTypeStore;
      return activityTypeStore.activityTypes.find(x => x.id === id) || activityTypeStore.activityTypes[0];
    },
    set(value) {
      return value.id;
    }
  }),
  assignees: types.array(types.model('Assignee', {
    id: types.string,
    type: types.string,
    name: types.maybeNull(types.string),
    email: types.string
  })),
  labels: types.array(types.model('Label', {
    id: types.string,
    name: types.string
  })),
  template: types.maybeNull(types.model({
    id: types.string,
    name: types.string
  }))
}).views(self => ({
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).actions(self => ({
  update(activity) {
    return callAgroApi<ActivityResponse>(`/activities/${self.id}`, 'PATCH', removeEmptyExtra(activity)).then(({
      data
    }) => ({
      ...Transformers.activity(data),
      activityType: data.activity_type.id,
      cropzone: data.cropzone?.id
    })).then(activity => self.root.uiStore.currentOrganization.pushToActivities([activity])[0]);
  },
  delete() {
    return callAgroApi<{
      message: string;
    }>(`/activities/${self.id}`, 'DELETE').then(() => self.root.uiStore.currentOrganization.destroyActivity(self));
  },
  deleteAttachment(attachmentId: string) {
    return callAgroApi(`/attachments/${attachmentId.toString()}`, 'DELETE');
  }
})).views(self => ({
  get isCustom() {
    return self.template !== null;
  },
  get datetimeZ() {
    const lang = self.root.userStore.user.settings.lang;
    return self.datetime.setZone(self.cropzone?.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang));
  },
  get plantingMethod() {
    return self.extra && self.extra?.get('method');
  },
  get endDatetimeZ() {
    const lang = self.root.userStore.user.settings.lang;
    return self.endDatetime?.setZone(self.cropzone?.timezone).reconfigure({
      outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory'
    }).setLocale(luxonUserLocale(lang)) || null;
  },
  get assigneeOption() {
    return self.assignees.length > 0 ? {
      label: self.assignees[0].name || self.assignees[0].email,
      value: self.assignees[0].id
    } : {
      label: 'farmer',
      value: undefined
    };
  },
  get actualHarvest() {
    return isPresent(self.extra.get('harvest')) ? parseFloat(self.extra.get('harvest')) : null;
  },
  get estimatedProduction() {
    return self.extra && self.extra.get('estimated_production');
  },
  get landPreparationMethod() {
    return self.extra && self.extra.get('method');
  },
  get coverCropPlantingType() {
    return self.extra && self.extra.get('type');
  },
  get pestDiseaseMethod() {
    return self.extra && self.extra.get('method');
  },
  get pestDiseaseType() {
    return self.extra && self.extra.get('type');
  },
  get timingDaysBeforePlanting() {
    return self.extra && self.extra.get('timing_days_before_planting');
  },
  get strawManagementOperationOption() {
    return {
      value: self.extra?.get('operation'),
      label: i18n.t(self.extra?.get('operation'))
    };
  },
  get strawManagementTypeOption() {
    return {
      value: self.extra?.get('type'),
      label: i18n.t(self.extra?.get('type'))
    };
  },
  get pestDiseaseProductName() {
    return self.extra && self.extra.get('product_name');
  },
  get amountOfSeeds() {
    return self.extra && self.extra.get('seed_usage');
  },
  get amountOfSeeds_unit() {
    return self.extra && self.extra.get('seed_usage_unit');
  },
  get amountOfSeedsComputed() {
    const areaUnit = self.root.userStore.user.settings.area_unit;
    return self.extra && self.extra.get('seed_usage') ? computeSeedAmountPerUnitArea(Number(self.extra && self.extra.get('seed_usage')) || 0, self.cropzone?.area || 0, String(areaUnit)) || 0 : 0;
  },
  get rating() {
    return self.extra && self.extra.get('rating');
  },
  get estimatedAreaLoss() {
    return self.extra && self.extra.get('estimated_area_loss');
  },
  get causeOfLoss() {
    return self.extra && self.extra.get('cause_of_loss');
  },
  get soilTexture() {
    return self.extra ? self.extra.get('soil_type') : '-';
  },
  get waterRegimeBeforePlanting() {
    return self.extra && self.extra.get('water_regime_before_planting');
  },
  get strawManagementType() {
    return self.extra && self.extra.get('type');
  },
  get strawManagementOperation() {
    return self.extra && self.extra.get('operation');
  },
  get waterRegimeWhilePlanting() {
    return self.extra && self.extra.get('water_regime_while_planting');
  },
  get firstFertilizer() {
    return self.extra.get('fertilizers')?.[0];
  }
})).views(self => ({
  get causeOfLossOption() {
    return {
      label: self.causeOfLoss,
      value: self.causeOfLoss
    };
  },
  get plantingMethodOption() {
    return {
      value: self.plantingMethod,
      label: i18n.t(self.plantingMethod)
    };
  },
  get soilColor() {
    return self.extra ? soilTypeOptions.find(option => option.value === self.extra.get('soil_type'))?.color : '#fff';
  },
  get soilOption() {
    return self.extra ? soilTypeOptions.find(option => option.value === self.extra.get('soil_type')) : soilTypeOptions[0];
  },
  get mapLayer() {
    return {
      id: self.id,
      datetimeZ: self.datetimeZ,
      soilTexture: self.soilTexture,
      isSample: false,
      point: null,
      cropzone_id: self.cropzone?.id,
      type: SupportedMapLayers.SOIL
    };
  },
  get fertilizerType() {
    return self.firstFertilizer?.get('type');
  },
  get fertilizerTypeOption() {
    return {
      value: self.firstFertilizer?.get('type'),
      label: i18n.t(self.firstFertilizer?.get('type'))
    };
  },
  get fertilizerMethod() {
    return self.firstFertilizer?.get('method');
  },
  get fertilizerMethodOption() {
    return {
      value: self.firstFertilizer?.get('method'),
      label: i18n.t(self.firstFertilizer?.get('method'))
    };
  },
  get coverCropPlantingOption() {
    return {
      value: self.extra && self.extra.get('type'),
      label: i18n.t(self.extra?.get('type'))
    };
  },
  get landPreparationMethodOption() {
    return {
      value: self.extra && self.extra.get('method'),
      label: i18n.t(self.extra?.get('method'))
    };
  },
  get pestDiseaseMethodOption() {
    return {
      value: self.extra && self.extra.get('method'),
      label: i18n.t(self.extra?.get('method'))
    };
  },
  get pestDiseaseTypeOption() {
    return {
      value: self.extra && self.extra.get('type'),
      label: i18n.t(self.extra?.get('type'))
    };
  },
  get timingDaysBeforePlantingOption() {
    return {
      value: self.extra && self.timingDaysBeforePlanting,
      label: i18n.t(self.timingDaysBeforePlanting)
    };
  },
  get fertilizerAmount(): number | null {
    return isPresent(self.firstFertilizer?.get('amount')) ? parseFloat(self.firstFertilizer?.get('amount')) : null;
  },
  get fertilizerUnit() {
    return self.firstFertilizer?.get('amount_unit')?.split('/')[0];
  },
  get fertilizerFormula() {
    return self.firstFertilizer !== undefined ? [self.firstFertilizer?.get('nitrogen'), self.firstFertilizer?.get('phosphorus'), self.firstFertilizer?.get('potassium')].map(x => isPresent(x) ? x : '?').join(' - ') : null;
  },
  get fertilizerNitrogen() {
    return self.firstFertilizer?.get('nitrogen');
  },
  get fertilizerPhosphorus() {
    return self.firstFertilizer?.get('phosphorus');
  },
  get fertilizerPotassium() {
    return self.firstFertilizer?.get('potassium');
  }
})));
export type IActivity = Instance<typeof Activity>;
export type IActivitySnapshotIn = SnapshotIn<typeof Activity>;